import { FC } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import { DailyNavOverrideSecurity } from 'api/models/DailyNavOverrideSecurity';
import { reviewPriceDialogColors as colors } from 'pages/Intelligence/DailyNav/components/ReviewPriceDialog/ReviewPriceDialog.styles';
import {
  canEnterInReporting,
  counterpartAmount,
  currencyCode,
  formatAmount,
  isEdited,
  isValidPrice,
  isZeroGridPrice,
  otherMode,
} from './overridePrice.utils';
import { OverridePriceTableProps } from './OverridePriceDialog.props';

const headerCellSx = {
  backgroundColor: colors.tableHeaderBackground,
  color: colors.slate700,
  fontSize: 11,
  fontWeight: 600,
};
const bodyCellSx = { color: colors.slate800, fontSize: 11 };

// An unheld common is listed because the company row prices it.
const heldByLabel = (security: DailyNavOverrideSecurity): string => {
  if (security.held_by.length) {
    return security.held_by.join(', ');
  }
  return security.is_common ? 'Company price' : '—';
};

const OverridePriceTable: FC<OverridePriceTableProps> = ({ form, securities, values, mode, disabled, onChange }) => {
  const code = currencyCode(form, mode);
  const showCounterpart = canEnterInReporting(form);
  const counterpartCode = currencyCode(form, otherMode(mode));

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headerCellSx}>Security</TableCell>
          <TableCell sx={headerCellSx}>Held by</TableCell>
          <TableCell align="right" sx={headerCellSx}>
            Current
          </TableCell>
          <TableCell align="right" sx={headerCellSx}>
            {`New (${code})`}
          </TableCell>
          {showCounterpart && (
            <TableCell align="right" sx={headerCellSx}>
              {counterpartCode}
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {securities.map(security => {
          const id = security.security_id;
          const value = values[id] ?? '';
          const invalid = !isValidPrice(value) || isZeroGridPrice(security, value);
          const edited = isEdited(security, values, form, mode);
          const current =
            security.cap_table_price == null
              ? null
              : Number(security.cap_table_price) * (mode === 'reporting' ? Number(form.currency.fx_rate) : 1);
          return (
            <TableRow key={id} data-testid={`override-price-row-${id}`}>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{security.name}</TableCell>
              <TableCell sx={{ ...bodyCellSx, color: colors.slate500 }}>
                {heldByLabel(security)}
              </TableCell>
              <TableCell align="right" sx={bodyCellSx}>
                {formatAmount(current, code)}
              </TableCell>
              <TableCell align="right" sx={{ ...bodyCellSx, width: 150 }}>
                <TextField
                  id={`override-price-input-${id}`}
                  value={value}
                  disabled={disabled}
                  error={invalid}
                  placeholder={security.cap_table_price == null ? 'Required' : undefined}
                  onChange={event => onChange(id, event.target.value)}
                  size="small"
                  slotProps={{
                    input: { sx: { fontSize: 11, fontWeight: edited ? 600 : 400 } },
                    htmlInput: { inputMode: 'decimal', 'aria-label': `New price for ${security.name}` },
                  }}
                />
              </TableCell>
              {showCounterpart && (
                <TableCell align="right" sx={{ ...bodyCellSx, color: colors.slate500 }}>
                  {formatAmount(counterpartAmount(value, form, mode), counterpartCode)}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default OverridePriceTable;
